import { createTheme} from '@mui/material/styles';
import { tokens, palette as p } from './tokens';

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: p.primary[400],
      light: p.primary[300],
      dark: p.primary[500],
      contrastText: '#000000',
    },
    secondary: {
      main: p.secondary[400],
      light: p.secondary[300],
      dark: p.secondary[700],
      contrastText: '#000000',
    },
    background: {
      default: '#121212',
      paper: '#1E1E1E',
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.87)',
      secondary: 'rgba(255, 255, 255, 0.6)',
      disabled: 'rgba(255, 255, 255, 0.38)',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    info: {
      main: '#5EB8FF',
      light: '#8ECCFF',
      dark: '#2E8FD1',
    },
    success: {
      main: p.feedback.success[500],
      light: p.feedback.success[100],
      dark: p.feedback.success[700],
    },
    warning: {
      main: p.feedback.warning[500],
      light: p.feedback.warning[100],
      dark: p.feedback.warning[700],
    },
    error: {
      main: p.feedback.error[500],
      light: p.feedback.error[100],
      dark: p.feedback.error[700],
    },
  },
  typography: {
    fontFamily: tokens.typography.fontFamily,
    /* Figma Foundations type scale */
    h1: { fontSize: '2rem', lineHeight: 1.25, fontWeight: 400 },
    h2: { fontSize: '1.75rem', lineHeight: 1.29, fontWeight: 400 },
    h3: { fontSize: '1.5rem', lineHeight: 1.33, fontWeight: 400 },
    h4: { fontSize: '1.25rem', lineHeight: 1.4, fontWeight: 400 },
    h5: { fontSize: '1.125rem', lineHeight: 1.2, fontWeight: 400 },
    h6: { fontSize: '1rem', lineHeight: 1.5, fontWeight: 500 },
    subtitle1: { fontSize: '1.0625rem', lineHeight: 1.53, fontWeight: 300 },
    subtitle2: { fontSize: '0.9375rem', lineHeight: 1.47, fontWeight: 500 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.47, fontWeight: 300 },
    body2: { fontSize: '0.875rem', lineHeight: 1.43, fontWeight: 300 },
    button: { fontSize: '0.9375rem', lineHeight: 1.33, fontWeight: 500, textTransform: 'none' as const },
    caption: { fontSize: '0.6875rem', lineHeight: 1.45, fontWeight: 400 },
    overline: { fontSize: '0.6875rem', lineHeight: 1.45, fontWeight: 500, textTransform: 'uppercase' as const },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.shape.borderRadius,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          transition: 'all 0.2s ease-in-out',
          backgroundColor: '#1E1E1E',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.shape.borderRadiusSmall,
          textTransform: 'none' as const,
          fontWeight: 500,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: tokens.shape.borderRadiusChip,
          fontWeight: 500,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 8,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E1E1E',
          color: 'rgba(255, 255, 255, 0.87)',
          boxShadow: 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1E1E1E',
          borderRight: '1px solid rgba(255, 255, 255, 0.12)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.shape.borderRadiusSmall,
          margin: '2px 8px',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: `rgba(65, 152, 133, 0.12)`,
            color: tokens.colors.primaryLight,
            '&:hover': {
              backgroundColor: `rgba(65, 152, 133, 0.16)`,
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: tokens.colors.primaryLight,
            },
            '&.Mui-focused fieldset': {
              borderColor: tokens.colors.primaryLight,
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          backgroundColor: '#1E1E1E',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
      },
    },
  },
  breakpoints: {
    values: tokens.grid.breakpoints,
  },
  spacing: 8,
});
